// src/components/Replan/Grid.js
import React, { useMemo } from 'react';
import { Line } from 'react-konva';
import { getSmartGridSpacing } from '../utils/drawingUtils';

// Hard safety cap on generated lines (degenerate zoom levels).
const MAX_LINES = 4000;

function buildWorldLines(spacing, worldX0, worldX1, worldY0, worldY1) {
  const arr = [];
  for (
    let x = Math.floor(worldX0 / spacing) * spacing;
    x <= worldX1;
    x += spacing
  ) {
    arr.push({ key: `v-${x}`, points: [x, worldY0, x, worldY1] });
  }
  for (
    let y = Math.floor(worldY0 / spacing) * spacing;
    y <= worldY1;
    y += spacing
  ) {
    arr.push({ key: `h-${y}`, points: [worldX0, y, worldX1, y] });
  }
  return arr;
}

function buildScreenLines(stepPx, stageScale, pos, width, height, worldX0, worldX1, worldY0, worldY1) {
  const arr = [];
  for (let sx = 0; sx <= width; sx += stepPx) {
    const wx = (sx - pos.x) / stageScale;
    arr.push({ key: `v-${sx}`, points: [wx, worldY0, wx, worldY1] });
  }
  for (let sy = 0; sy <= height; sy += stepPx) {
    const wy = (sy - pos.y) / stageScale;
    arr.push({ key: `h-${sy}`, points: [worldX0, wy, worldX1, wy] });
  }
  return arr;
}

/**
 * Grid component that renders a CAD-style graph-paper pattern on the stage.
 *
 * Two modes, resolved from whether a real scale is available:
 * - world (document loaded): major = smart real-unit spacing, minor = major/5,
 *   world-anchored so lines scroll with the model.
 * - screen (no document): fixed 24px minor / 120px major, screen-anchored.
 */
const Grid = ({
  currentScaleProps,
  dpi,
  stageScale = 1,
  stagePos = { x: 0, y: 0 },
  containerSize = { width: 0, height: 0 },
  measurementSystem = 'metric',
  stroke = 'rgba(128, 128, 128, 0.28)',
  minorStroke = 'rgba(128, 128, 128, 0.14)',
  strokeWidth = 1,
}) => {
  const { width, height } = containerSize;

  // Zoom-only: spacing changes with scale, not pan.
  const spacing = useMemo(() => {
    if (!stageScale || stageScale <= 0) return null;
    if (currentScaleProps && dpi) {
      const major = getSmartGridSpacing(
        currentScaleProps,
        dpi,
        stageScale,
        measurementSystem,
      );
      if (!major || major <= 0) return null;
      return {
        kind: 'world',
        major,
        minor: major / 5,
        showMinor: major * stageScale >= 25,
      };
    }
    return { kind: 'screen', major: 120, minor: 24, showMinor: true };
  }, [currentScaleProps, dpi, stageScale, measurementSystem]);

  // Quantize pan so the expensive line memo only recomputes when the viewport
  // crosses a major line. Screen mode tracks the raw position (its lines are
  // anchored to screen pixels, so they must rebuild on every pan/zoom).
  const quantStep = spacing?.kind === 'world' ? spacing.major * stageScale : 0;
  const qx = useMemo(
    () => (quantStep > 0 ? Math.floor(stagePos.x / quantStep) * quantStep : stagePos.x),
    [quantStep, stagePos.x],
  );
  const qy = useMemo(
    () => (quantStep > 0 ? Math.floor(stagePos.y / quantStep) * quantStep : stagePos.y),
    [quantStep, stagePos.y],
  );

  const { lines, minorLines } = useMemo(() => {
    if (!spacing || width <= 0 || height <= 0) {
      return { lines: [], minorLines: [] };
    }

    const pos = { x: qx, y: qy };
    const pad = spacing.kind === 'world' ? spacing.major : 0;

    const worldX0 = -pos.x / stageScale - pad;
    const worldX1 = (width - pos.x) / stageScale + pad;
    const worldY0 = -pos.y / stageScale - pad;
    const worldY1 = (height - pos.y) / stageScale + pad;

    if (spacing.kind === 'world') {
      const majorLines = buildWorldLines(
        spacing.major,
        worldX0,
        worldX1,
        worldY0,
        worldY1,
      );
      if (majorLines.length > MAX_LINES) return { lines: [], minorLines: [] };
      const minorLines = spacing.showMinor
        ? buildWorldLines(spacing.minor, worldX0, worldX1, worldY0, worldY1)
        : [];
      return {
        lines: majorLines,
        minorLines: minorLines.length > MAX_LINES ? [] : minorLines,
      };
    }

    const majorLines = buildScreenLines(
      spacing.major,
      stageScale,
      pos,
      width,
      height,
      worldX0,
      worldX1,
      worldY0,
      worldY1,
    );
    const minorLines = buildScreenLines(
      spacing.minor,
      stageScale,
      pos,
      width,
      height,
      worldX0,
      worldX1,
      worldY0,
      worldY1,
    );
    return {
      lines: majorLines,
      minorLines: minorLines.length > MAX_LINES ? [] : minorLines,
    };
  }, [spacing, qx, qy, stageScale, width, height]);

  const lineWidth = strokeWidth / stageScale;

  return (
    <>
      {minorLines.map((line) => (
        <Line
          key={`minor-${line.key}`}
          points={line.points}
          stroke={minorStroke}
          strokeWidth={lineWidth}
          listening={false}
          perfectDrawEnabled={false}
        />
      ))}
      {lines.map((line) => (
        <Line
          key={`major-${line.key}`}
          points={line.points}
          stroke={stroke}
          strokeWidth={lineWidth}
          listening={false}
          perfectDrawEnabled={false}
        />
      ))}
    </>
  );
};

export default Grid;
